import type { WidgetAppConfig } from '@pypestream/pype/customization';

import type { Channel, PlatformMessages } from '../utils';
import type {
  PypeBeta,
  PypeStyle,
} from '../xstate/prostudio.api-service.types';
import type { MessageKey } from './default-messages';
import type { DesignLoad } from './load-pype-next-design';

/**
 * Project Bobcat — "Copy settings…" between two channels of one solution.
 *
 * Two things can be copied, each through the save path its own editor uses:
 *
 * - Default messages live on the channel's PYPE (`pype.autoresponders`), saved
 *   with `updateDefaultMessages { id: pypeId, env }`. Two channels on the same
 *   pype already share one set, so that pair can't copy messages.
 * - Styling lives on the channel (widget) itself, in two stores:
 *   1. Channel Settings' branding: the widget's `style`, `beta.customStyling`
 *      and `custom_pype_css`, saved with `updateChannel`. `mutatePutWidget*`
 *      REPLACES all three, so the target channel is spread through and only
 *      the fields below change (same approach as the Pype Designer's rename).
 *   2. The Pype Next designer's widget app design (`WidgetAppConfig`), saved
 *      with `mutateWidgetAppDesign` — only when the `pype-next` flag is on.
 *
 * Copied is look and feel only. Never copied: names and text labels (the
 * widget's display name, launcher name, the design's display name), and
 * anything that identifies or connects the channel (ids, app id, bot binding,
 * live-agent routing, environment, URLs and keys). Images: the legacy branding
 * copies the banner and brand moment (`COPIED_STYLE_FIELDS`,
 * `COPIED_CUSTOM_STYLING_FIELDS`) and never the logo; the Pype Next design
 * copies its customer logo, banner and brand moment
 * (`COPIED_DESIGN_BRANDING_FIELDS`).
 */

/** `PypeStyle` fields copied — every field except the display `name`. */
export const COPIED_STYLE_FIELDS = [
  'widgetPosition',
  'shouldLockRatio',
  'widgetHeight',
  'widgetWidth',
  'widgetStyleColor',
  'widgetStyleTextColor',
  'agentBubbleColor',
  'agentBubbleTextColor',
  'userBubbleColor',
  'userBubbleTextColor',
  'bannerUrl',
] as const satisfies readonly (keyof PypeStyle)[];

/** `beta.customStyling` fields copied — everything but `launcherName`. */
export const COPIED_CUSTOM_STYLING_FIELDS = [
  'buttonColor',
  'textColor',
  'brandMomentUrl',
] as const satisfies readonly (keyof PypeBeta['customStyling'])[];

/** Pype Next `branding` fields copied — images, not text labels. */
export const COPIED_DESIGN_BRANDING_FIELDS = [
  'customerLogoUrl',
  'brandMomentUrl',
] as const satisfies readonly (keyof WidgetAppConfig['branding'])[];

const STYLE_LABELS: Record<(typeof COPIED_STYLE_FIELDS)[number], string> = {
  widgetPosition: 'Widget position',
  shouldLockRatio: 'Lock aspect ratio',
  widgetHeight: 'Widget height',
  widgetWidth: 'Widget width',
  widgetStyleColor: 'Header color',
  widgetStyleTextColor: 'Header text color',
  agentBubbleColor: 'Agent bubble color',
  agentBubbleTextColor: 'Agent bubble text color',
  userBubbleColor: 'User bubble color',
  userBubbleTextColor: 'User bubble text color',
  bannerUrl: 'Banner image',
};

const CUSTOM_STYLING_LABELS: Record<
  (typeof COPIED_CUSTOM_STYLING_FIELDS)[number],
  string
> = {
  buttonColor: 'Button color',
  textColor: 'Button text color',
  brandMomentUrl: 'Brand moment image',
};

const MESSAGE_LABELS: Record<MessageKey, string> = {
  offline: 'Agents offline',
  busy: 'Agents busy',
  technicalError: 'Technical error',
};

export interface SettingChange {
  label: string;
  from: string;
  to: string;
}

const show = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  return String(value);
};

type LegacyStyling = Pick<Channel, 'style' | 'beta' | 'customPypeCss'>;

/**
 * The target's legacy branding with the source's look and feel copied over.
 * Target fields that aren't copied (its display name, launcher name) are kept.
 */
export const copyLegacyStyling = (
  source: LegacyStyling,
  target: LegacyStyling
): Required<LegacyStyling> => {
  const style = { ...(target.style ?? {}) } as PypeStyle;
  COPIED_STYLE_FIELDS.forEach((field) => {
    if (source.style && field in source.style) {
      (style as Record<string, unknown>)[field] = source.style[field];
    }
  });
  if (style.name === undefined) style.name = '';

  const targetCustom = target.beta?.customStyling;
  const customStyling = {
    ...(targetCustom ?? {}),
  } as PypeBeta['customStyling'];
  COPIED_CUSTOM_STYLING_FIELDS.forEach((field) => {
    const value = source.beta?.customStyling?.[field];
    if (value !== undefined) customStyling[field] = value;
  });

  return {
    style,
    beta: { ...(target.beta ?? {}), customStyling } as PypeBeta,
    customPypeCss: source.customPypeCss ?? '',
  };
};

/** What `copyLegacyStyling` would change on the target, for the confirm step. */
export const diffLegacyStyling = (
  source: LegacyStyling,
  target: LegacyStyling
): SettingChange[] => {
  const next = copyLegacyStyling(source, target);
  const changes: SettingChange[] = [];

  COPIED_STYLE_FIELDS.forEach((field) => {
    const from = target.style?.[field];
    const to = next.style[field];
    if (show(from) !== show(to)) {
      changes.push({
        label: STYLE_LABELS[field],
        from: show(from),
        to: show(to),
      });
    }
  });
  COPIED_CUSTOM_STYLING_FIELDS.forEach((field) => {
    const from = target.beta?.customStyling?.[field];
    const to = next.beta.customStyling?.[field];
    if (show(from) !== show(to)) {
      changes.push({
        label: CUSTOM_STYLING_LABELS[field],
        from: show(from),
        to: show(to),
      });
    }
  });
  if ((target.customPypeCss ?? '') !== next.customPypeCss) {
    const lines = (css: string) =>
      css ? `${css.split('\n').length} line(s)` : '(empty)';
    changes.push({
      label: 'Custom CSS',
      from: lines(target.customPypeCss ?? ''),
      to: lines(next.customPypeCss),
    });
  }
  return changes;
};

/**
 * The target's Pype Next design with the source's appearance copied over:
 * theme, tokens, part overrides, raw CSS, shell (modality, size) and the
 * branding images. The target's display name and launcher name stay.
 */
export const copyPypeNextDesign = (
  source: WidgetAppConfig,
  target: WidgetAppConfig
): WidgetAppConfig => {
  const branding = { ...target.branding };
  // Generic per field, so each copy keeps that field's own type.
  const copyField = <K extends keyof WidgetAppConfig['branding']>(field: K) => {
    branding[field] = source.branding[field];
  };
  COPIED_DESIGN_BRANDING_FIELDS.forEach(copyField);
  return {
    ...target,
    design: structuredClone(source.design),
    shell: structuredClone(source.shell),
    branding,
  };
};

export type DesignCopyPlan =
  | {
      copy: true;
      config: WidgetAppConfig;
      /** The target had no saved design, so the copy starts from the defaults. */
      seededFromDefaults: boolean;
    }
  | {
      copy: false;
      reason: 'source-absent' | 'source-failed' | 'target-failed';
    };

/**
 * What to do with the Pype Next design part of a copy, given how each end's
 * design loaded. A target that FAILED to load is never treated as having no
 * design: seeding it from the defaults would overwrite a live design with
 * whatever the copy does not carry over. Only a target that is `absent` is seeded.
 * `seed` is only called for that case.
 */
export const resolveDesignCopy = (
  source: DesignLoad,
  target: DesignLoad,
  seed: () => WidgetAppConfig
): DesignCopyPlan => {
  if (source.status === 'failed')
    return { copy: false, reason: 'source-failed' };
  if (source.status === 'absent')
    return { copy: false, reason: 'source-absent' };
  if (target.status === 'failed')
    return { copy: false, reason: 'target-failed' };
  const seeded = target.status === 'absent';
  return {
    copy: true,
    config: copyPypeNextDesign(
      source.config,
      target.status === 'loaded' ? target.config : seed()
    ),
    seededFromDefaults: seeded,
  };
};

/** The designs loaded for one pair of channels, tagged with the pair. */
export interface LoadedDesigns {
  sourceId: string;
  targetId: string;
  source: DesignLoad;
  target: DesignLoad;
}

/**
 * The loaded designs, only when they were loaded for exactly this pair. A
 * result that arrives for an earlier pair is never read as the current one's.
 */
export const designsForPair = (
  designs: LoadedDesigns | null,
  source: { id: string } | null,
  target: { id: string } | null
): LoadedDesigns | null =>
  designs &&
  source &&
  target &&
  designs.sourceId === source.id &&
  designs.targetId === target.id
    ? designs
    : null;

/**
 * True while the Pype Next part of a copy has no plan yet. The Copy button and
 * `apply` both ask it, so the button cannot be enabled where `apply` would
 * return early, nor the other way round.
 */
export const awaitsDesignPlan = (
  doStyling: boolean,
  pypeNext: boolean,
  plan: DesignCopyPlan | null
): boolean => doStyling && pypeNext && !plan;

/** The confirm step's line about the Pype Next part. */
export const describeDesignCopy = (
  plan: DesignCopyPlan,
  sourceName: string,
  targetName: string
): string => {
  if (plan.copy) {
    return plan.seededFromDefaults
      ? `${targetName} has no Pype Next design saved, so it starts from the defaults and gets ${sourceName}'s theme, tokens, part overrides, CSS, layout and images.`
      : 'Pype Next design (theme, tokens, part overrides, CSS, layout and images) will also be copied.';
  }
  switch (plan.reason) {
    case 'source-absent':
      return `${sourceName} has no Pype Next design saved, so that part is skipped.`;
    case 'source-failed':
      return `Couldn't load ${sourceName}'s Pype Next design, so it will not be copied. Go back and review again to retry.`;
    case 'target-failed':
      return `Couldn't load ${targetName}'s Pype Next design, so it will not be copied and the saved design stays as it is. Go back and review again to retry.`;
  }
};

/**
 * The same skip, worded for the result shown after the modal has closed: there
 * is no Back button then. Empty when nothing needs saying (the design was
 * copied, or the source simply has no design, which the confirm step stated).
 */
export const describeDesignCopyResult = (
  plan: DesignCopyPlan,
  sourceName: string,
  targetName: string
): string => {
  if (plan.copy) return '';
  switch (plan.reason) {
    case 'source-absent':
      return '';
    case 'source-failed':
      return `Couldn't load ${sourceName}'s Pype Next design, so it was not copied. Open Copy settings again to retry.`;
    case 'target-failed':
      return `Couldn't load ${targetName}'s Pype Next design, so it was not copied and the saved design was left as it is. Open Copy settings again to retry.`;
  }
};

/** The source's messages, as-is, become the target pype's messages. */
export const diffMessages = (
  source: PlatformMessages,
  target: PlatformMessages
): SettingChange[] =>
  (Object.keys(MESSAGE_LABELS) as MessageKey[]).flatMap((key) => {
    const changes: SettingChange[] = [];
    if (source[key].text !== target[key].text) {
      changes.push({
        label: MESSAGE_LABELS[key],
        from: show(target[key].text),
        to: show(source[key].text),
      });
    }
    if (source[key].enable !== target[key].enable) {
      changes.push({
        label: `${MESSAGE_LABELS[key]} (on/off)`,
        from: show(target[key].enable),
        to: show(source[key].enable),
      });
    }
    return changes;
  });

export interface CopyAvailability {
  available: boolean;
  /** Why not, when it isn't. */
  reason?: string;
}

/** Whether default messages can be copied from `source` to `target`. */
export const messagesAvailability = (
  source: Pick<Channel, 'pypeId'>,
  target: Pick<Channel, 'pypeId'>,
  hasMessages: (pypeId: string) => boolean
): CopyAvailability => {
  if (source.pypeId === target.pypeId) {
    return {
      available: false,
      reason:
        'Both channels are on the same pype, so they already share messages.',
    };
  }
  if (!hasMessages(source.pypeId) || !hasMessages(target.pypeId)) {
    return {
      available: false,
      reason: 'No default messages found for one of these channels.',
    };
  }
  return { available: true };
};

/** Whether styling can be copied from `source` to `target`. */
export const stylingAvailability = (
  source: Pick<Channel, 'type'>,
  target: Pick<Channel, 'type'>
): CopyAvailability =>
  source.type === 'twilio_voice' || target.type === 'twilio_voice'
    ? { available: false, reason: 'Voice channels have no widget styling.' }
    : { available: true };
